import React, {KeyboardEvent} from 'react';
import {ActiveTabState, NavTabsState, TabState} from './types.ts';
import {activateTab, isActive} from './tabs.ts';
import {ReactStateSetter} from '../../../types.ts';
import Icon from '../Icon.tsx';
import {useSearchParams} from 'react-router-dom';
import classNames from 'classnames';
import useConfirmLeave from '../../../hooks/useConfirmLeave.ts';

interface NavTabLinkProps {
    tab: TabState;
    navTabsState: NavTabsState;
    setActiveTabState: ReactStateSetter<ActiveTabState>;
}

/*
 * One tab: a real button with role="tab", so it is keyboard reachable and
 * screen readers announce it as part of the tab list. Left and right arrows
 * move between the tabs in the same list.
 */
const NavTabLink: React.FC<NavTabLinkProps> = ({
    tab,
    navTabsState,
    setActiveTabState
}) => {
    const [, setSearchParams] = useSearchParams();
    const active = isActive(navTabsState, tab.name);
    // Another tab unmounts this one, and with it any unsaved editor work.
    const confirmLeave = useConfirmLeave();

    const onSelectedOptionChange = async (selectedOption: string) => {
        const currentActiveTabName = navTabsState.activeTabName;
        if (currentActiveTabName != tab.name && !(await confirmLeave())) {
            return;
        }
        setActiveTabState({activeTabName: tab.name, selectedOption});
        tab.onOptionSelected && tab.onOptionSelected(selectedOption);

        if (currentActiveTabName != tab.name) {
            setSearchParams({tab: tab.name});
        }
    };

    const activate = async () => {
        if (!active && !(await confirmLeave())) {
            return;
        }
        activateTab(navTabsState, setActiveTabState, tab.name);
        setSearchParams({tab: tab.name});
    };

    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
            return;
        }

        const list = event.currentTarget.closest('[role="tablist"]');
        if (!list) {
            return;
        }

        const tabs = Array.from(
            list.querySelectorAll<HTMLButtonElement>('[role="tab"]')
        );
        const index = tabs.indexOf(event.currentTarget);
        const step = event.key === 'ArrowRight' ? 1 : -1;
        const next = tabs[(index + step + tabs.length) % tabs.length];
        next?.focus();
        next?.click();
        event.preventDefault();
    };

    return (
        <li
            className={classNames('nav-item', {'ms-auto': tab.msAuto})}
            role="presentation"
            style={tab.style}
        >
            <button
                type="button"
                role="tab"
                aria-selected={active}
                tabIndex={active ? 0 : -1}
                className={classNames('nav-link', {active})}
                onClick={activate}
                onKeyDown={onKeyDown}
            >
                {tab.icon && <Icon icon={tab.icon} size="s" />}
                <span className="tab-text">{tab.text}</span>
                {tab.count !== undefined && (
                    <span className="mx-tab-count">{tab.count}</span>
                )}
            </button>
            {tab.options && (
                <div className="channel-select-holder">
                    <select
                        className="form-select form-select-sm"
                        aria-label={`${tab.text} channel`}
                        value={'#' + tab.selectedOption}
                        onChange={e => onSelectedOptionChange(e.target.value)}
                    >
                        {tab.options.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </li>
    );
};

export default NavTabLink;
